import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FiLayers, FiInfo, FiChevronRight, FiChevronLeft } from "react-icons/fi";
import axiosInstance from "../../../../api/axiosInstance";

const CategoryDetails = () => {
  const { id } = useParams();

  const [category, setCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(""); // Fixed useState syntax

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const servicesPerPage = 9;

  useEffect(() => {
    const fetchCategoryDetails = async () => {
      try {
        const response = await axiosInstance.get(`/users/categories/${id}`);
        const cat = response.data?.category || {};
        const servicesList = response.data?.services || [];
        // console.log("CATEGORY SERVICES:", servicesList);

        const formattedServices = servicesList.map((srv) => ({
          ...srv,
          image: srv.image || srv.thumbnail || srv.imageSrc || null,
        }));

        setCategory(cat);
        setServices(formattedServices);
      } catch (err) {
        console.error("❌ Error fetching category:", err);
        setError("Failed to load category details");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchCategoryDetails();
  }, [id]);

  // --- Pagination Calculation ---
  const indexOfLastService = currentPage * servicesPerPage;
  const indexOfFirstService = indexOfLastService - servicesPerPage;
  const currentServices = services.slice(indexOfFirstService, indexOfLastService);
  const totalPages = Math.ceil(services.length / servicesPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#134DA7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-outfit">
      
      {/* Dark Hero Section for Category Details */}
      <section className="relative pt-24 pb-24 px-6 bg-[#020617] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="bg" />
        </div>
        
        <div className="container mx-auto relative z-10 max-w-4xl text-center">
          {/* Breadcrumbs */}
          <nav className="flex items-center justify-center gap-2 text-sm font-medium text-slate-300 mb-8">
            <Link to="/home" className="hover:text-white transition-colors flex items-center gap-1">
              Home
            </Link>
            <FiChevronRight size={14} className="opacity-50" />
            <Link to="/categories" className="hover:text-white transition-colors">
              Categories
            </Link>
            <FiChevronRight size={14} className="opacity-50" />
            <span className="text-white">{category?.title || "Loading..."}</span>
          </nav>

          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
            {category?.title}
          </h1>
          <p className="text-slate-300 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            {category?.description || "Explore our specialized services tailored to meet your professional needs."}
          </p>
        </div>
      </section>

      <div className="container mx-auto max-w-7xl px-6 py-12 -mt-8 relative z-20">

        {/* Section Divider */}
        <div className="mb-8 flex items-center justify-between border-b border-gray-200 pb-4">
          <h2 className="text-lg font-bold text-gray-800 tracking-tight">Services Offered</h2>
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
            {services.length} {services.length === 1 ? "Service" : "Services"} Total
          </span>
        </div>

        {/* Services Grid */}
        {currentServices.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentServices.map((service, index) => (
                <Link
                  to={`/services/${service.id || service._id}`}
                  key={service.id || service._id || index}
                  className="group"
                >
                  <div className="bg-white h-full rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden">
                    <div className="h-48 w-full bg-gray-50 relative overflow-hidden">
                      {service.image ? (
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-200">
                          <FiLayers size={40} />
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex flex-col h-full">
                      <h3 className="text-lg font-bold text-[#134DA7] leading-snug mb-3 group-hover:text-blue-700">
                        {service.title || service.name}
                      </h3>
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-6">
                        {service.description || "Expertly crafted service solutions tailored for your success."}
                      </p>
                      
                      <div className="mt-auto flex items-center text-sm font-bold text-[#134DA7]">
                        View Details 
                        <FiChevronRight className="ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* --- Pagination Controls --- */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center items-center gap-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <FiChevronLeft size={20} />
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                      currentPage === i + 1
                        ? "bg-[#134DA7] text-white shadow-lg shadow-blue-100"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-[#134DA7] hover:text-[#134DA7]"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <FiChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
            <FiInfo size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No services found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryDetails;